/**
 * @fileoverview Timesheet analytics dashboard (MOR-906 §7.8)
 *
 * Owns the scope and period the built-in cards are read over — "My time" or
 * firm-wide (admins/owners), across a preset or a custom range — and hands
 * the resulting cards to the board, which decides where they sit and whether
 * they are there at all.
 *
 * The metrics themselves live in overviewCards.tsx; this is the controls and
 * the single `analytics` read behind them.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import {
  addDays,
  endOfMonth,
  format,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { LayoutGrid } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { OverviewCardKey } from "@cvx/timesheets/timesheetProfiles/schema";

import { extractErrorMessage } from "@/utils/errors";
import { Button } from "@/ui/primitives";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/ui/composed/menus";
import type { AnalyticsAnswer } from "./AskAnalyticsBox";
import { FavouriteReportsBoard } from "./FavouriteReportsBoard";
import { formatDateRange } from "./format";
import {
  OVERVIEW_CARD_ORDER,
  OVERVIEW_CARD_TITLES,
  buildOverviewCards,
} from "./overviewCards";

const RANGE_PRESETS = [
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30", label: "Last 30 days" },
  { key: "custom", label: "Custom range" },
] as const;
type RangeKey = (typeof RANGE_PRESETS)[number]["key"];

interface DateRange {
  start: string;
  end: string;
}

function rangeFor(key: Exclude<RangeKey, "custom">): DateRange {
  const now = new Date();
  const iso = (d: Date) => format(d, "yyyy-MM-dd");
  switch (key) {
    case "this_week": {
      const start = startOfWeek(now, { weekStartsOn: 1 });
      return { start: iso(start), end: iso(addDays(start, 6)) };
    }
    case "last_week": {
      const start = subDays(startOfWeek(now, { weekStartsOn: 1 }), 7);
      return { start: iso(start), end: iso(addDays(start, 6)) };
    }
    case "this_month":
      return { start: iso(startOfMonth(now)), end: iso(endOfMonth(now)) };
    case "last_month": {
      const lastMonth = subMonths(now, 1);
      return {
        start: iso(startOfMonth(lastMonth)),
        end: iso(endOfMonth(lastMonth)),
      };
    }
    case "last_30":
      return { start: iso(subDays(now, 29)), end: iso(now) };
  }
}

export function TimesheetAnalytics({
  orgId,
  isAdmin,
  onEditCard,
}: {
  orgId: Id<"organizations">;
  isAdmin: boolean;
  /** A pinned card's spec, for the page to reopen the builder on. */
  onEditCard?: (answer: AnalyticsAnswer) => void;
}) {
  const [scope, setScope] = useState<"me" | "org">(isAdmin ? "org" : "me");
  const [rangeKey, setRangeKey] = useState<RangeKey>("this_week");
  // Seeded from whichever preset was showing when the user asked for a custom
  // range, so the pickers open on real dates rather than an empty period.
  const [custom, setCustom] = useState<DateRange>(() => rangeFor("this_week"));
  const { start, end } = rangeKey === "custom" ? custom : rangeFor(rangeKey);
  const today = format(new Date(), "yyyy-MM-dd");

  /** Keep the pair in order: moving one end past the other takes it along. */
  const setCustomEnd = (which: "start" | "end") => (isoDate: string) => {
    if (!isoDate) return;
    setCustom((prev) =>
      which === "start"
        ? { start: isoDate, end: prev.end < isoDate ? isoDate : prev.end }
        : { start: prev.start > isoDate ? isoDate : prev.start, end: isoDate },
    );
  };

  const { data } = useQuery(
    convexQuery(api.timesheets.reports.analytics, {
      orgId,
      startDate: start,
      endDate: end,
      scope,
    }),
  );
  const { data: board } = useQuery(
    convexQuery(api.timesheets.board.getBoard, { orgId }),
  );
  const setOverviewCard = useMutation(api.timesheets.board.setOverviewCard);

  const overview = useMemo(
    () => (data ? buildOverviewCards({ data, scope, start, end, today }) : []),
    [data, end, scope, start, today],
  );

  const isPinned = (key: OverviewCardKey) =>
    board?.overviewCards.find((c) => c.key === key)?.pinned ?? true;

  // The board renders these inside the overview panel, so the scope, the
  // period and the Cards menu sit with the cards they act on — and not with
  // the saved widgets and tables below, which carry their own periods.
  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      {isAdmin && (
        <div className="flex items-center rounded-md border border-border">
          <Button
            variant={scope === "org" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 rounded-r-none"
            onClick={() => setScope("org")}
          >
            Firm
          </Button>
          <Button
            variant={scope === "me" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 rounded-l-none"
            onClick={() => setScope("me")}
          >
            My time
          </Button>
        </div>
      )}
      <Select
        value={rangeKey}
        onValueChange={(value) => {
          if (value === "custom") setCustom({ start, end });
          setRangeKey(value as RangeKey);
        }}
      >
        <SelectTrigger className="h-8 w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RANGE_PRESETS.map((p) => (
            <SelectItem key={p.key} value={p.key}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {rangeKey === "custom" ? (
        <>
          <DatePicker
            value={custom.start}
            onChange={setCustomEnd("start")}
            className="w-36"
            inputClassName="h-8"
            placeholder="From"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <DatePicker
            value={custom.end}
            onChange={setCustomEnd("end")}
            className="w-36"
            inputClassName="h-8"
            placeholder="To"
          />
          {data?.truncated && (
            <span className="text-xs text-muted-foreground">
              large range truncated
            </span>
          )}
        </>
      ) : (
        <span className="text-xs text-muted-foreground">
          {formatDateRange(start, end)}
          {data?.truncated && " · large range truncated"}
        </span>
      )}

      {/* Removing a built-in card has to be undoable somewhere, and the
          board itself cannot show what is no longer on it. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="ml-auto h-8">
            <LayoutGrid className="size-4" />
            Cards
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Built-in cards</DropdownMenuLabel>
          {OVERVIEW_CARD_ORDER.map((key) => (
            <DropdownMenuCheckboxItem
              key={key}
              checked={isPinned(key)}
              onCheckedChange={(checked) =>
                setOverviewCard({ orgId, key, pinned: checked }).catch(
                  (err: unknown) =>
                    toast.error(
                      extractErrorMessage(err, "Could not update the board."),
                    ),
                )
              }
            >
              {OVERVIEW_CARD_TITLES[key]}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <FavouriteReportsBoard
      orgId={orgId}
      overview={overview}
      overviewLoading={data === undefined}
      controls={controls}
      onEdit={onEditCard}
    />
  );
}
